import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Activity, AlertTriangle, Bot, CheckCircle2, History, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { List, Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { ActivityScopeType } from './types.js';

const { Text } = Typography;

export interface RecentActivityItem {
  id?: string;
  dealId?: string;
  title?: string;
  message?: string;
  timestamp?: string;
  scopeType?: ActivityScopeType;
  type?: ActivityScopeType;
}

export interface RecentActivityWidgetProps {
  items?: readonly RecentActivityItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onItemClick?: (item: RecentActivityItem) => void;
}

function timeAgo(timestamp: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60_000);

  if (minutes < 1) {
    return t('crm.dashboard.time.just_now');
  }

  if (minutes < 60) {
    return t('crm.dashboard.time.minutes_ago', { count: minutes });
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('crm.dashboard.time.hours_ago', { count: hours });
  }

  return t('crm.dashboard.time.days_ago', { count: Math.floor(hours / 24) });
}

const ACTIVITY_ICONS: Record<string, LucideIcon> = {
  session: Bot,
  task: CheckCircle2,
  error: AlertTriangle,
  system: Activity,
  pipeline: Zap,
};

function getActivityColor(scopeType: string): string {
  switch (scopeType) {
    case 'session':
      return '#8b5cf6'; // violet
    case 'task':
      return '#16a34a'; // success green
    case 'error':
      return '#dc2626'; // error red
    case 'pipeline':
      return '#d97706'; // warning amber
    default:
      return '#2563eb'; // info blue
  }
}

export function RecentActivityWidget({
  items = [],
  className,
  testId,
  loading = false,
  onItemClick,
}: RecentActivityWidgetProps): ReactElement {
  const { t } = useTranslation();
  
  const widgetContainerProps = {
    title: t('crm.dashboard.activity.title'),
    icon: History,
    minHeight: 300,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#a8a29e' }}>
        {t('crm.dashboard.activity.events_count', { count: items.length })}
      </span>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse p-4 flex flex-col gap-3">
          {[1, 2, 3, 4].map((index) => (
            <div key={index} className="flex flex-col gap-2 bg-stone-100/50 p-2 rounded">
              <div className="h-3 w-3/4 bg-stone-200/50 rounded" />
              <div className="h-2 w-1/2 bg-stone-200/50 rounded" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-6 opacity-50 min-h-[220px]">
          <History size={28} className="mb-2 text-stone-400" />
          <Text type="secondary" style={{ fontSize: 12 }}>{t('crm.dashboard.activity.empty')}</Text>
        </div>
      ) : (
        <List
          size="small"
          dataSource={items as RecentActivityItem[]}
          renderItem={(item) => {
            const scopeType = item.scopeType ?? item.type ?? 'system';
            const Icon = ACTIVITY_ICONS[scopeType] ?? Activity;
            const color = getActivityColor(scopeType);

            return (
              <List.Item style={{ padding: 0 }}>
                <button
                  type="button"
                  onClick={onItemClick ? () => onItemClick(item) : undefined}
                  className="transition-colors text-left border-0 flex w-full items-start gap-3"
                  style={{
                    cursor: onItemClick ? 'pointer' : 'default',
                    opacity: onItemClick ? 1 : 0.8,
                    padding: '10px 16px',
                    borderBottom: '1px solid #f5f5f4',
                    background: 'transparent',
                  }}
                >
                  <div
                    className="rounded flex items-center justify-center shrink-0 mt-0.5"
                    style={{ width: 28, height: 28, background: `${color}12`, color }}
                  >
                    <Icon size={14} style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Text strong className="truncate block" style={{ fontSize: 13, color: '#44403c', lineHeight: 1.3 }}>
                      {item.title ?? item.message ?? t('crm.dashboard.activity.fallback_title')}
                    </Text>
                    {item.message && item.title ? (
                       <Text type="secondary" className="block mt-0.5" style={{ fontSize: 11, lineHeight: 1.4, color: '#78716c' }}>
                         {item.message}
                       </Text>
                    ) : null}
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#a8a29e', whiteSpace: 'nowrap', marginTop: 2 }}>
                    {item.timestamp ? timeAgo(item.timestamp, t) : ''}
                  </span>
                </button>
              </List.Item>
            );
          }}
        />
      )}
    </WidgetContainer>
  );
}
